import React, { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel } from "@/components/kit";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Settings as SettingsIcon, Loader2, Mail, KeyRound } from "lucide-react";

export default function Settings() {
  const { user, refreshMe } = useAuth();
  const [ownerName, setOwnerName] = useState(user?.name || "");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [pwErr, setPwErr] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const needsPassword = !!user?.needs_password;

  useEffect(() => {
    api.get("/profile").then(({ data }) => {
      if (data?.phone) setPhone(data.phone);
      if (data?.owner_name) setOwnerName((n) => n || data.owner_name);
    }).catch(() => {});
  }, []);

  const saveAccount = async () => {
    setSaving(true);
    try { await api.put("/profile", { data: { owner_name: ownerName, phone } }); toast.success("Saved."); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const savePassword = async () => {
    setPwErr("");
    if (pw.length < 8) return setPwErr("Password must be at least 8 characters.");
    if (pw !== pw2) return setPwErr("Passwords don't match.");
    setSavingPw(true);
    try {
      await api.post("/auth/set-password", { password: pw });
      setPw(""); setPw2("");
      await refreshMe();
      toast.success("Password saved. You can now log in with it anytime.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSavingPw(false); }
  };

  return (
    <div className="mm-fade-in max-w-2xl">
      <PageHeader icon={SettingsIcon} title="Settings" subtitle="Manage your account." />

      <Panel className="mb-6">
        <div className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-4"><Mail className="h-4 w-4 text-emerald-400" /> Account</div>
        <div className="space-y-1.5 mb-4">
          <Label className="text-xs">Email</Label>
          <Input value={user?.email || ""} disabled className="bg-slate-950 border-slate-700 h-10 text-slate-400" />
        </div>
        <div className="space-y-1.5 mb-4">
          <Label className="text-xs">Display name</Label>
          <Input data-testid="settings-name" value={ownerName} onChange={(e) => setOwnerName(e.target.value)} className="bg-slate-950 border-slate-700 h-10" />
        </div>
        <div className="space-y-1.5 mb-4">
          <Label className="text-xs">Phone number</Label>
          <Input data-testid="settings-phone" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="e.g. +1 (555) 123-4567" className="bg-slate-950 border-slate-700 h-10" />
          <p className="text-[11px] text-slate-500">Optional. If you add it, we'll keep it synced with your CRM.</p>
        </div>
        <Button data-testid="settings-save-btn" onClick={saveAccount} disabled={saving} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
          {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save changes"}
        </Button>
      </Panel>

      <Panel className={needsPassword ? "border-emerald-500/40 bg-emerald-500/5" : ""}>
        <div className="flex items-center gap-2 text-sm font-medium text-slate-300 mb-1"><KeyRound className="h-4 w-4 text-emerald-400" /> {needsPassword ? "Set your password" : "Change password"}</div>
        <p className="text-sm text-slate-400 mb-4">
          {needsPassword
            ? "Set a password now so you can log back into your account anytime."
            : "Choose a new password for your account."}
        </p>
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div className="space-y-1.5">
            <Label className="text-xs">New password</Label>
            <Input data-testid="settings-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Min 8 characters" className="bg-slate-950 border-slate-700 h-10" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Confirm password</Label>
            <Input data-testid="settings-password-confirm" type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} className="bg-slate-950 border-slate-700 h-10" />
          </div>
        </div>
        {pwErr && <p data-testid="settings-password-error" className="text-sm text-rose-400 mb-3">{pwErr}</p>}
        <Button data-testid="settings-password-btn" onClick={savePassword} disabled={savingPw || !pw} className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold">
          {savingPw ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save password"}
        </Button>
      </Panel>
    </div>
  );
}
